import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "src/components/base/breadcrumb";
import { Heading } from "src/components/base/heading";
import { Divider } from "src/components/base/divider";
import { Text } from "src/components/base/text";
import { ApiInternal } from "src/api/api_internal";
import { Link } from "src/components/base/link";
import { ConfirmDialog } from "src/components/base/confirm-dialog";
import { Button } from "src/components/base/button";
import { notify } from "src/components/base/toast";
import { Badge } from "src/components/base/badge";
import {
    ComputerDesktopIcon,
    ExclamationTriangleIcon,
    KeyIcon,
    ServerStackIcon,
    UsersIcon,
} from "@heroicons/react/20/solid";
import { clsx } from "clsx";

/**
 * The properties for {@link TenantDeletePreview}
 */
export type TenantDeletePreviewProps = {};

/**
 * Tenant delete preview
 */
export default function TenantDeletePreview(props: TenantDeletePreviewProps) {
    const [t] = useTranslation("tenant");

    const { preview, tenant } = Route.useLoaderData();
    const navigate = useNavigate();
    const [openConfirm, setOpenConfirm] = React.useState(false);

    const deleteBadge = (count: number) =>
        count > 0 ? <Badge color={"red"}>{t("label.will-be-deleted")}</Badge> : undefined;

    return (
        <div className={"flex flex-col gap-8"}>
            <div className={"flex flex-col gap-3"}>
                <Breadcrumbs
                    items={[
                        { label: t("button.tenants"), to: "/a/tenants" },
                        {
                            label: tenant.name,
                            to: "/a/tenants/$tenantId/general",
                            params: { tenantId: tenant.uuid },
                        },
                        { label: t("label.delete") },
                    ]}
                />
                <Heading>{t("heading.delete-tenant")}</Heading>
                <Divider />
                <div
                    className={
                        "flex items-center gap-3 rounded-(--radius-card) bg-red-50 p-4 ring-1 ring-red-600/20 dark:bg-red-500/10 dark:ring-red-500/30"
                    }
                >
                    <ExclamationTriangleIcon className={"size-5 shrink-0 text-red-600 dark:text-red-400"} />
                    <div className={"flex flex-col gap-1"}>
                        <span className={"text-sm/6 font-medium text-red-800 dark:text-red-300"}>
                            {t("heading.delete-warning")}
                        </span>
                        <span className={"text-sm/6 text-red-700/80 dark:text-red-400/80"}>
                            {t("description.delete-warning")}
                        </span>
                    </div>
                </div>
            </div>

            <div className={"grid gap-4 sm:grid-cols-2"}>
                <Link
                    href={"/a/devices"}
                    search={{ search: "", filter: "server-only", tenant: tenant.uuid }}
                    className={"block"}
                >
                    <StatRow
                        icon={<ServerStackIcon data-slot="icon" />}
                        label={t("label.server-device-count")}
                        value={preview.server_device_count}
                        badge={deleteBadge(preview.server_device_count)}
                        sub={<Text>{t("description.delete-server-device")}</Text>}
                        className={"h-full cursor-pointer transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"}
                    />
                </Link>
                <Link
                    href={"/a/devices"}
                    search={{ search: "", filter: "clients-only", tenant: tenant.uuid }}
                    className={"block"}
                >
                    <StatRow
                        icon={<ComputerDesktopIcon data-slot="icon" />}
                        label={t("label.user-device-count")}
                        value={preview.user_device_count}
                        badge={deleteBadge(preview.user_device_count)}
                        sub={<Text>{t("description.delete-user-device")}</Text>}
                        className={"h-full cursor-pointer transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"}
                    />
                </Link>
                <Link href={"/a/preauthkeys"} className={"block"}>
                    <StatRow
                        icon={<KeyIcon data-slot="icon" />}
                        label={t("label.preauthkey-count")}
                        value={preview.preauthkey_count}
                        badge={deleteBadge(preview.preauthkey_count)}
                        sub={<Text>{t("description.delete-preauthkey")}</Text>}
                        className={"h-full cursor-pointer transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"}
                    />
                </Link>
                <Link href={"/a/users"} className={"block"}>
                    <StatRow
                        icon={<UsersIcon data-slot="icon" />}
                        label={t("label.user-count")}
                        value={preview.user_count}
                        badge={deleteBadge(preview.user_count)}
                        sub={<Text>{t("description.delete-user")}</Text>}
                        className={"h-full cursor-pointer transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/50"}
                    />
                </Link>
            </div>

            <div className={"flex justify-end"}>
                <Button color={"red"} onClick={() => setOpenConfirm(true)}>
                    {t("button.confirm-delete")}
                </Button>
            </div>

            <Suspense>
                <ConfirmDialog
                    open={openConfirm}
                    title={t("heading.confirm-delete-tenant", { tenant_name: tenant.name })}
                    description={t("description.delete-tenant")}
                    confirmLabel={t("button.confirm-delete")}
                    onClose={() => setOpenConfirm(false)}
                    onConfirm={async () => {
                        await ApiInternal.tenants.delete(tenant.uuid);
                        notify.success(t("toast.successfully-deleted"));
                        await navigate({ to: "/a/tenants" });
                    }}
                />
            </Suspense>
        </div>
    );
}

/**
 * The properties for {@link StatRow}
 */
export type StatRowProps = {
    /** The label for the stat */
    label: string;

    /** The value to display */
    value: React.ReactNode;

    /** The leading icon shown left of the label and value (any heroicon-style svg with `data-slot="icon"`). */
    icon: React.ReactNode;

    /** Optional badge rendered right of the value */
    badge?: React.ReactNode;

    /** Item to display below the value */
    sub?: React.ReactNode;

    /** Override the default class names. */
    className?: string;
};

/**
 * A horizontal variant stat, the icon sits on the left, label and
 * value are stacked right of it, and an optional badge is rendered inline next
 * to the value.
 */
function StatRow({ label, value, icon, badge, sub, className }: StatRowProps) {
    return (
        <div
            className={clsx(
                className,
                "rounded-(--radius-card) bg-(--surface-card) p-5 shadow-(--shadow-card-sm) ring-1 ring-zinc-950/5 transition-shadow hover:shadow-(--shadow-card-md) dark:ring-white/10",
            )}
        >
            <div className={"flex items-start gap-4"}>
                <div
                    className={
                        "inline-flex size-9 shrink-0 items-center justify-center rounded-(--radius-control) bg-(--color-brand-50) text-(--color-brand-600) *:data-[slot=icon]:size-5 dark:bg-(--color-brand-900)/40 dark:text-(--color-brand-300)"
                    }
                >
                    {icon}
                </div>
                <div className={"flex flex-col"}>
                    <span className={"text-sm/6 font-medium text-zinc-600 dark:text-zinc-300"}>{label}</span>
                    <span className={"mt-1 flex items-end gap-2"}>
                        <span className={"text-2xl/8 font-semibold text-zinc-950 dark:text-white"}>{value}</span>
                        {badge}
                    </span>
                    {sub && <span className={"mt-2 text-xs/5 text-zinc-500 dark:text-zinc-400"}>{sub}</span>}
                </div>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/$tenantId/delete-preview")({
    component: TenantDeletePreview,
    loader: async ({ params }) => {
        const [preview, tenant] = await Promise.all([
            ApiInternal.tenants.getDeletePreview(params.tenantId),
            ApiInternal.tenants.getSingle(params.tenantId),
        ]);
        return { preview, tenant };
    },
});
